/**
 * Confirmation Dialog Component
 * Displays a modal for confirming critical MCP actions
 */

import React, { useEffect } from 'react';
import { Modal, Button, Descriptions, notification } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import type { ConfirmationDetails } from '@/api/types/mcp.types';
import './ConfirmationDialog.css';

interface ConfirmationDialogProps {
  details: ConfirmationDetails;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  details,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  // Auto-cancel after 5 minutes
  useEffect(() => {
    const timeout = setTimeout(
      () => {
        onCancel();
        notification.warning({
          message: 'Confirmation Timeout',
          description: 'The confirmation request has expired.',
        });
      },
      5 * 60 * 1000
    ); // 5 minutes

    return () => clearTimeout(timeout);
  }, [onCancel]);

  return (
    <Modal
      open={true}
      title={
        <div className="confirmation-title">
          <ExclamationCircleOutlined
            style={{ color: '#faad14', marginRight: 8 }}
          />
          <span>Confirm Action</span>
        </div>
      }
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          danger
          onClick={onConfirm}
          loading={loading}
        >
          Confirm
        </Button>,
      ]}
      className="mcp-confirmation-dialog"
      maskClosable={!loading}
    >
      <p className="confirmation-message">{details.confirmation_message}</p>

      {details.tool_description && (
        <div className="confirmation-tool-info">
          <strong>Action:</strong> {details.tool_description}
        </div>
      )}

      {details.tool_args && Object.keys(details.tool_args).length > 0 && (
        <div className="confirmation-details">
          <Descriptions column={1} size="small" bordered>
            {Object.entries(details.tool_args).map(([key, value]) => (
              <Descriptions.Item key={key} label={key}>
                {typeof value === 'object'
                  ? JSON.stringify(value)
                  : String(value)}
              </Descriptions.Item>
            ))}
          </Descriptions>
        </div>
      )}

      <div className="confirmation-warning">
        ⚠️ This action requires your confirmation. It will timeout in 5 minutes.
      </div>
    </Modal>
  );
};
